import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Search, Filter, Coffee, Calendar, Clock, User } from 'lucide-react';
import { doctorBreakService } from '../../services/doctorBreakService';
import { DoctorBreak, Doctor, DoctorScheduleExtended } from '../../lib/supabase';
import { doctorService } from '../../lib/services';
import { doctorScheduleExtendedService } from '../../services/doctorScheduleExtendedService';
import toast from 'react-hot-toast';

const Breaks: React.FC = () => {
  const [breaks, setBreaks] = useState<DoctorBreak[]>([]);
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [schedules, setSchedules] = useState<DoctorScheduleExtended[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingBreak, setEditingBreak] = useState<DoctorBreak | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [doctorFilter, setDoctorFilter] = useState<string>('all');

  const [formData, setFormData] = useState({
    doctor_id: '',
    schedule_id: '',
    break_type: 'one_time' as 'one_time' | 'recurring',
    start_date: '',
    end_date: '',
    start_time: '',
    end_time: '',
    reason: '',
    status: 'active' as 'active' | 'cancelled',
    notes: ''
  });

  useEffect(() => {
    loadBreaks();
    loadDoctors();
    loadSchedules();
  }, []);

  const loadBreaks = async () => {
    try {
      setLoading(true);
      const data = await doctorBreakService.getAll();
      setBreaks(data);
    } catch (error) {
      console.error('Error loading breaks:', error);
      toast.error('Failed to load breaks');
    } finally {
      setLoading(false);
    }
  };

  const loadDoctors = async () => {
    try {
      const data = await doctorService.getAll();
      setDoctors(data);
    } catch (error) {
      console.error('Error loading doctors:', error);
    }
  };

  const loadSchedules = async () => {
    try {
      const data = await doctorScheduleExtendedService.getAll();
      setSchedules(data);
    } catch (error) {
      console.error('Error loading schedules:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingBreak) {
        await doctorBreakService.update(editingBreak.id, formData);
        toast.success('Break updated successfully');
      } else {
        await doctorBreakService.create(formData);
        toast.success('Break created successfully');
      }
      resetForm();
      loadBreaks();
    } catch (error) {
      console.error('Error saving break:', error);
      toast.error('Failed to save break');
    }
  };

  const handleEdit = (breakItem: DoctorBreak) => {
    setEditingBreak(breakItem);
    setFormData({
      doctor_id: breakItem.doctor_id || '',
      schedule_id: breakItem.schedule_id || '',
      break_type: breakItem.break_type,
      start_date: breakItem.start_date,
      end_date: breakItem.end_date || '',
      start_time: breakItem.start_time,
      end_time: breakItem.end_time,
      reason: breakItem.reason || '',
      status: breakItem.status,
      notes: breakItem.notes || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this break?')) return;
    
    try {
      await doctorBreakService.delete(id);
      toast.success('Break deleted successfully');
      loadBreaks();
    } catch (error) {
      console.error('Error deleting break:', error);
      toast.error('Failed to delete break');
    }
  };

  const resetForm = () => {
    setFormData({
      doctor_id: '',
      schedule_id: '',
      break_type: 'one_time',
      start_date: '',
      end_date: '',
      start_time: '',
      end_time: '',
      reason: '',
      status: 'active',
      notes: ''
    });
    setEditingBreak(null);
    setShowForm(false);
  };

  const filteredBreaks = breaks.filter(breakItem => {
    const matchesSearch = (breakItem.doctor?.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (breakItem.reason || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDoctor = doctorFilter === 'all' || breakItem.doctor_id === doctorFilter;
    return matchesSearch && matchesDoctor;
  });

  const formatTime = (time: string) => {
    return new Date(`2000-01-01T${time}`).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString();
  };

  const getFilteredSchedules = () => {
    return schedules.filter(schedule => schedule.doctor_id === formData.doctor_id);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Doctor Breaks</h1>
          <p className="text-gray-600">Manage doctor breaks and time-offs</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Break
        </button>
      </div>

      {/* Search and Filter */}
      <div className="flex gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search breaks..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
          />
        </div>
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <select
            value={doctorFilter}
            onChange={(e) => setDoctorFilter(e.target.value)}
            className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
          >
            <option value="all">All Doctors</option>
            {doctors.map(doctor => (
              <option key={doctor.id} value={doctor.id}>{doctor.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Breaks Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredBreaks.map((breakItem) => (
          <div key={breakItem.id} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-2">
                <Coffee className="w-5 h-5 text-orange-500" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {breakItem.doctor?.name || 'Unknown Doctor'}
                  </h3>
                  <p className="text-sm text-gray-600">{breakItem.doctor?.specialization}</p>
                </div>
              </div>
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                breakItem.status === 'active' 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-red-100 text-red-800'
              }`}>
                {breakItem.status}
              </span>
            </div>

            <div className="space-y-3 mb-4">
              <div className="flex items-center gap-2 text-sm">
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  breakItem.break_type === 'one_time' 
                    ? 'bg-blue-100 text-blue-800' 
                    : 'bg-purple-100 text-purple-800'
                }`}>
                  {breakItem.break_type === 'one_time' ? 'One Time' : 'Recurring'}
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Calendar className="w-4 h-4" />
                <span>
                  {formatDate(breakItem.start_date)}
                  {breakItem.end_date && ` - ${formatDate(breakItem.end_date)}`}
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Clock className="w-4 h-4" />
                <span>{formatTime(breakItem.start_time)} - {formatTime(breakItem.end_time)}</span>
              </div>
              {breakItem.reason && (
                <div className="text-sm text-gray-600">
                  <span className="font-medium">Reason:</span> {breakItem.reason}
                </div>
              )}
            </div>

            {breakItem.notes && (
              <div className="bg-gray-50 rounded-lg p-3 mb-4">
                <p className="text-sm text-gray-700">{breakItem.notes}</p>
              </div>
            )}

            <div className="flex justify-end gap-2">
              <button
                onClick={() => handleEdit(breakItem)}
                className="p-2 text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
              >
                <Edit className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleDelete(breakItem.id)}
                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {filteredBreaks.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">No breaks found</p>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold mb-4">
              {editingBreak ? 'Edit Break' : 'Add Break'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Doctor *
                </label>
                <select
                  required
                  value={formData.doctor_id}
                  onChange={(e) => setFormData({ ...formData, doctor_id: e.target.value, schedule_id: '' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                >
                  <option value="">Select Doctor</option>
                  {doctors.map((doctor) => (
                    <option key={doctor.id} value={doctor.id}>
                      {doctor.name} - {doctor.specialization}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Schedule (Optional)
                </label>
                <select
                  value={formData.schedule_id}
                  onChange={(e) => setFormData({ ...formData, schedule_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                >
                  <option value="">No specific schedule</option>
                  {getFilteredSchedules().map((schedule) => (
                    <option key={schedule.id} value={schedule.id}>
                      {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][schedule.day_of_week]} - {schedule.start_time} to {schedule.end_time}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Break Type *
                </label>
                <select
                  required
                  value={formData.break_type}
                  onChange={(e) => setFormData({ ...formData, break_type: e.target.value as any })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                >
                  <option value="one_time">One Time</option>
                  <option value="recurring">Recurring</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Date *
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Time *
                  </label>
                  <input
                    type="time"
                    required
                    value={formData.start_time}
                    onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    End Time *
                  </label>
                  <input
                    type="time"
                    required
                    value={formData.end_time}
                    onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason
                </label>
                <input
                  type="text"
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="e.g., Lunch break, Meeting, etc."
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                >
                  <option value="active">Active</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  rows={3}
                  placeholder="Additional notes"
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
                >
                  {editingBreak ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Breaks;
